<!-- SizePicker — which bag, and how many.

     A segmented row of the coffee's real offers ("250g · $22"), wrapping when a
     roaster sells five sizes and a bundle. Sold-out sizes stay visible and
     disabled with "Sold out", so the range reads honestly. Under it, a
     quantity stepper, 1–20 (the bag's own ceiling).

     v-model:size and v-model:qty. The add itself lives in `useAddToBag` below,
     shared by the Label sheet and the Coffee page so the button behaves the
     same in both. -->
<template>
  <div class="picker">
    <fieldset class="sizes">
      <legend class="picker__label">Size</legend>
      <div class="sizes__row" :class="{ 'sizes__row--wrap': offers.length > 3 }">
        <label
          v-for="o in offers"
          :key="o.size"
          class="size"
          :class="{ 'size--on': o.size === size, 'size--out': !o.inStock }"
        >
          <input
            type="radio"
            class="sr-only"
            :name="groupName"
            :value="o.size"
            :checked="o.size === size"
            :disabled="!o.inStock"
            @change="pick(o)"
          >
          <span class="size__main">{{ o.size }}</span>
          <span class="size__price"><span class="sr-only">, </span>{{ money(o.price) }}</span>
          <span v-if="!o.inStock" class="size__out">Sold out</span>
        </label>
      </div>
    </fieldset>

    <div class="qty" role="group" aria-labelledby="qty-label">
      <span id="qty-label" class="picker__label">Quantity</span>
      <div class="qty__stepper">
        <button type="button" class="qty__btn" :disabled="qty <= 1" aria-label="One fewer" @click="step(-1)">
          <IonIcon :icon="removeOutline" aria-hidden="true" />
        </button>
        <output class="qty__n" aria-live="polite" :aria-label="`Quantity ${qty}`">{{ qty }}</output>
        <button type="button" class="qty__btn" :disabled="qty >= 20" aria-label="One more" @click="step(1)">
          <IonIcon :icon="addOutline" aria-hidden="true" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { addToBag } from '../store.js';
import { useAction } from '../core/ui/index.js';
import { haptic } from '../core/util.js';
import { announce } from '../core/a11y.js';

export const money = (n) => (Number.isInteger(n) ? `$${n}` : `$${Number(n).toFixed(2)}`);

/** The first size you can actually buy; none when everything is sold out. */
export const defaultSize = (coffee) => coffee?.offers?.find((o) => o.inStock)?.size || '';

/**
 * The Add-to-bag button's whole behaviour, for one coffee (a ref or getter).
 *
 *   size, qty          the picker's model
 *   offer              the chosen offer
 *   label              "Add to bag — $22" | "In your bag ✓ · Add another" | "Sold out"
 *   add                a useAction: busy/error/run
 *
 * On success: a light haptic, a screen-reader line, and a window event
 * `bag:added` ({ slug }) so the tab bar can animate its badge.
 */
export function useAddToBag (coffeeRef) {
  const coffee = computed(() => (typeof coffeeRef === 'function' ? coffeeRef() : coffeeRef.value));
  const size = ref(defaultSize(coffee.value));
  const qty = ref(1);
  const added = ref(false);

  const offer = computed(() => coffee.value?.offers?.find((o) => o.size === size.value) || null);
  const soldOut = computed(() => !coffee.value?.offers?.some((o) => o.inStock));

  watch(() => coffee.value?.slug, () => {
    size.value = defaultSize(coffee.value);
    qty.value = 1;
    added.value = false;
  });
  watch([size, qty], () => { added.value = false; });

  const add = useAction(async () => {
    const c = coffee.value;
    if (!c || !offer.value) throw new Error('Choose a size first.');
    addToBag(c.slug, offer.value.size, qty.value);
    haptic('tick');
    announce(`Added ${qty.value > 1 ? `${qty.value} × ` : ''}${offer.value.size} ${c.name} to your bag.`);
    window.dispatchEvent(new CustomEvent('bag:added', { detail: { slug: c.slug } }));
    // After the watcher above has seen nothing change, so the state sticks.
    added.value = true;
  }, { weight: 'none', failed: 'Couldn\'t add that to your bag. Try again.' });

  const label = computed(() => {
    if (soldOut.value) return 'Sold out';
    if (add.busy.value) return 'Adding…';
    if (added.value) return 'In your bag ✓ · Add another';
    return `Add to bag — ${money((offer.value?.price || 0) * qty.value)}`;
  });

  return { size, qty, offer, soldOut, added, add, label };
}
</script>

<script setup>
import { IonIcon } from '@ionic/vue';
import { addOutline, removeOutline } from 'ionicons/icons';

const props = defineProps({
  offers: { type: Array, required: true },
  size: { type: String, default: '' },
  qty: { type: Number, default: 1 },
});
const emit = defineEmits(['update:size', 'update:qty']);

const groupName = `size-${Math.random().toString(36).slice(2, 8)}`;

function pick (o) {
  if (!o.inStock || o.size === props.size) return;
  haptic('selection');
  emit('update:size', o.size);
}
function step (d) {
  const n = Math.max(1, Math.min(20, props.qty + d));
  if (n === props.qty) return;
  haptic('selection');
  emit('update:qty', n);
}
</script>

<style scoped>
.picker { display: grid; gap: var(--sp-4); }

.picker__label {
  display: block;
  margin: 0 0 var(--sp-2);
  padding: 0;
  color: var(--muted, #5E6B60);
  font-family: var(--font-mono, 'Martian Mono', ui-monospace, monospace);
  font-size: calc(var(--text-xs) * 0.9);
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.sizes { margin: 0; padding: 0; border: 0; min-width: 0; }

/* Sizes are chips: sage when free, ink when chosen. The size in Manrope, its price in the clips' mono. */
.sizes__row {
  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
  gap: var(--sp-2);
}
/* Five sizes and a bundle do not fit one row at 390pt: two columns instead. */
.sizes__row--wrap { grid-auto-flow: row; grid-template-columns: 1fr 1fr; }

.size {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-height: calc(var(--sp-7) + var(--sp-2));
  padding: var(--sp-2);
  border-radius: var(--radius);
  background: var(--sage, #E6EDE4);
  color: var(--ink, #1F2A22);
  text-align: center;
  cursor: pointer;
  transition: background var(--dur) var(--ease), color var(--dur) var(--ease), transform 120ms var(--ease);
  -webkit-tap-highlight-color: transparent;
}
.size:active:not(.size--out) { transform: scale(0.97); }
.size__main { font-size: var(--text-md); font-weight: 800; line-height: 1.2; }
.size__price {
  font-family: var(--font-mono, 'Martian Mono', ui-monospace, monospace);
  font-size: var(--text-xs);
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: var(--ink-2, #3E4A41);
}
.size--on { background: var(--ink, #1F2A22); color: var(--dock, #FAF7F1); }
.size--on .size__price { color: var(--mustard, #F2C230); }
.size--out { cursor: not-allowed; background: transparent; box-shadow: inset 0 0 0 1px var(--line, #ECE6DC); }
.size--out .size__main, .size--out .size__price { color: var(--faint, #8A948B); text-decoration: line-through; text-decoration-thickness: 1px; }
.size__out { color: var(--muted, #5E6B60); font-size: var(--text-xs); font-weight: 700; }
.size:has(input:focus-visible) { outline: 2px solid var(--ink, #1F2A22); outline-offset: 2px; }
@media (prefers-reduced-motion: reduce) { .size, .size:active { transform: none; transition: none; } }

.qty { display: flex; align-items: center; justify-content: space-between; gap: var(--sp-4); }
.qty .picker__label { margin: 0; }
.qty__stepper {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: var(--sage, #E6EDE4);
}
.qty__btn {
  display: grid;
  place-items: center;
  width: var(--tap);
  height: var(--tap);
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: none;
  color: var(--ink, #1F2A22);
  font-size: var(--text-lg);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.qty__btn:disabled { opacity: 0.3; cursor: default; }
.qty__btn:focus-visible { outline: 2px solid var(--ink, #1F2A22); outline-offset: -2px; }
.qty__btn:not(:disabled):active { background: color-mix(in srgb, var(--ink, #1F2A22) 10%, transparent); }
.qty__n {
  min-width: var(--sp-6);
  color: var(--ink, #1F2A22);
  font-family: var(--font-mono, 'Martian Mono', ui-monospace, monospace);
  font-size: var(--text-md);
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  text-align: center;
}
</style>
